// JOBE-0105: ByDepartmentBlock — side-rail block on the /projects index.
// Aggregates the visible-page rows by `relations.belongsToDepartment[0]`
// (falling back to `relations.inDepartment[0]` — the JOBE-0106 home
// page reads this field, mirror the same fallback here) and renders the
// top-5 departments as click-to-filter rows.
//
// Click handler: appends a `dept:<urn>` chip when chipVocabulary's
// `dept` friendly token is BFF-wired; today it's `bffWired: false` so
// the click is a no-op with a tooltip ("Filter by department — coming
// soon"). When the BFF widens to honour `dept:`, the token flips to
// `bffWired: true` and the click becomes a one-line chip append — no
// other change needed.

import React, { useMemo } from 'react';
import { topNByKey } from '../aggregations.js';
import { FRIENDLY_TOKENS } from '../../library/chipVocabulary.js';

const TOP_N = 5;

function urnSlug(urn) {
  if (!urn || typeof urn !== 'string') return '';
  const idx = urn.lastIndexOf(':');
  return idx >= 0 ? urn.slice(idx + 1) : urn;
}

function deptOf(row) {
  const relations = (row && (row.relations
    || (row.payload && row.payload.relations))) || {};
  const dept = relations.belongsToDepartment || relations.inDepartment;
  if (Array.isArray(dept) && dept.length > 0) return dept[0];
  return null;
}

export function ByDepartmentBlock({ rows, onAppendChip }) {
  const top = useMemo(() => topNByKey(rows || [], deptOf, TOP_N), [rows]);
  const max = top.reduce((m, e) => (e.count > m ? e.count : m), 0);
  const deptEntry = FRIENDLY_TOKENS.dept;
  const deptWired = deptEntry && deptEntry.bffWired === true;
  const tooltip = deptWired ? '' : 'Filter by department — coming soon';

  const onPick = (deptUrn) => {
    if (!deptWired) return; // no-op until BFF wires `dept:`
    if (!onAppendChip || !deptUrn) return;
    onAppendChip({
      kind: 'predicate',
      friendlyToken: 'dept',
      wirePredicate: deptEntry.wirePredicate,
      values: [deptUrn],
    });
  };

  return (
    <section
      className="projects-rail-block projects-rail-by-dept"
      data-testid="projects-rail-by-department"
    >
      <header className="projects-rail-block-header">
        <h3 className="projects-rail-block-title">By department</h3>
        <span className="projects-rail-block-sub muted">top {TOP_N}</span>
      </header>
      {top.length === 0 ? (
        <p className="muted projects-rail-empty" data-testid="projects-rail-by-department-empty">
          (none yet)
        </p>
      ) : (
        <ul className="projects-rail-list">
          {top.map((entry) => {
            const widthPct = max > 0 ? Math.round((entry.count / max) * 100) : 0;
            return (
              <li key={entry.key}>
                <button
                  type="button"
                  className={`projects-rail-row projects-rail-bar-row${deptWired ? '' : ' projects-rail-row-disabled'}`}
                  onClick={() => onPick(entry.key)}
                  title={tooltip || undefined}
                  data-testid={`projects-rail-by-department-${urnSlug(entry.key)}`}
                  data-bff-wired={deptWired ? 'true' : 'false'}
                >
                  <span className="projects-rail-row-label">{urnSlug(entry.key)}</span>
                  <span
                    className="projects-rail-bar"
                    style={{ width: `${widthPct}%` }}
                    aria-hidden="true"
                  />
                  <span className="projects-rail-row-count">{entry.count}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
